/**
 * Tetrish - Controls Component
 * Handles keyboard and touch input for game control
 */

export class Controls {
	constructor(game) {
		this.game = game;

		// Key mappings
		this.keyMap = {
			'ArrowLeft': this.moveLeft.bind(this),
			'ArrowRight': this.moveRight.bind(this),
			'ArrowDown': this.moveDown.bind(this),
			'ArrowUp': this.rotate.bind(this),
			' ': this.hardDrop.bind(this), // Space
			'c': this.holdPiece.bind(this),
			'C': this.holdPiece.bind(this),
			'p': this.togglePause.bind(this),
			'P': this.togglePause.bind(this),
		};

		// Touch controls
		this.touchStartX = 0;
		this.touchStartY = 0;
		this.swipeThreshold = 30; // Minimum distance for swipe

		// Auto-repeat settings
		this.keyDownInterval = null;
		this.repeatDelay = 150; // ms

		// Bind methods
		this.handleKeyDown = this.handleKeyDown.bind(this);
		this.handleKeyUp = this.handleKeyUp.bind(this);
		this.handleTouchStart = this.handleTouchStart.bind(this);
		this.handleTouchMove = this.handleTouchMove.bind(this);
		this.handleTouchEnd = this.handleTouchEnd.bind(this);

		// Set up event listeners
		this.setupEventListeners();
	}

	/**
	 * Set up event listeners
	 */
	setupEventListeners() {
		// Keyboard events
		document.addEventListener('keydown', this.handleKeyDown);
		document.addEventListener('keyup', this.handleKeyUp);

		// Touch events
		const gameBoard = document.getElementById('game-board');
		if (gameBoard) {
			gameBoard.addEventListener('touchstart', this.handleTouchStart, { passive: false });
			gameBoard.addEventListener('touchmove', this.handleTouchMove, { passive: false });
			gameBoard.addEventListener('touchend', this.handleTouchEnd);
		}
	}

	/**
	 * Clean up event listeners
	 */
	cleanup() {
		document.removeEventListener('keydown', this.handleKeyDown);
		document.removeEventListener('keyup', this.handleKeyUp);

		const gameBoard = document.getElementById('game-board');
		if (gameBoard) {
			gameBoard.removeEventListener('touchstart', this.handleTouchStart);
			gameBoard.removeEventListener('touchmove', this.handleTouchMove);
			gameBoard.removeEventListener('touchend', this.handleTouchEnd);
		}
	}

	/**
	 * Handle key down events
	 */
	handleKeyDown(event) {
		// Ignore key presses if modal is open
		if (document.querySelector('.modal:not([hidden])')) {
			return;
		}

		const key = event.key;

		// Special handling for pause key
		if (key === 'p' || key === 'P') {
			event.preventDefault();
			this.togglePause();
			return;
		}

		// Check if we have a handler for this key
		if (this.keyMap[key]) {
			// Prevent default only for game keys
			event.preventDefault();

			// Execute the key action
			this.keyMap[key]();

			// Set up auto-repeat for movement keys
			if (key === 'ArrowLeft' || key === 'ArrowRight' || key === 'ArrowDown') {
				// Clear any existing interval
				clearInterval(this.keyDownInterval);

				// Set up a new auto-repeat interval
				this.keyDownInterval = setInterval(() => {
					this.keyMap[key]();
				}, this.repeatDelay);
			}
		}
	}

	/**
	 * Handle key up events
	 */
	handleKeyUp(event) {
		// Clear auto-repeat interval on key up
		clearInterval(this.keyDownInterval);
	}

	/**
	 * Handle touch start
	 */
	handleTouchStart(event) {
		event.preventDefault();

		if (!this.game.state.isGameRunning || this.game.state.isPaused) {
			return;
		}

		// Get the initial touch position
		const touch = event.touches[0];
		this.touchStartX = touch.clientX;
		this.touchStartY = touch.clientY;
	}

	/**
	 * Handle touch move
	 */
	handleTouchMove(event) {
		event.preventDefault();

		if (!this.game.state.isGameRunning || this.game.state.isPaused) {
			return;
		}
	}

	/**
	 * Handle touch end
	 */
	handleTouchEnd(event) {
		if (!this.game.state.isGameRunning || this.game.state.isPaused) {
			return;
		}

		// Only process if there was an actual touch
		if (event.changedTouches.length === 0) {
			return;
		}

		const touch = event.changedTouches[0];
		const deltaX = touch.clientX - this.touchStartX;
		const deltaY = touch.clientY - this.touchStartY;

		// Determine if this was a tap or swipe
		const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

		if (distance < this.swipeThreshold) {
			// This was a tap - rotate the piece
			this.rotate();
		} else {
			// This was a swipe - determine direction
			const absX = Math.abs(deltaX);
			const absY = Math.abs(deltaY);

			if (absX > absY) {
				// Horizontal swipe
				if (deltaX > 0) {
					this.moveRight();
				} else {
					this.moveLeft();
				}
			} else {
				// Vertical swipe
				if (deltaY > 0) {
					// Swipe down - hard drop
					this.hardDrop();
				} else {
					// Swipe up - hold piece
					this.holdPiece();
				}
			}
		}
	}

	/**
	 * Move the piece left
	 */
	moveLeft() {
		this.game.movePiece(-1, 0);
	}

	/**
	 * Move the piece right
	 */
	moveRight() {
		this.game.movePiece(1, 0);
	}

	/**
	 * Move the piece down (soft drop)
	 */
	moveDown() {
		const currentPiece = this.game.state.currentPiece;

		if (currentPiece) {
			currentPiece.move(0, 1);

			if (this.game.board.checkCollision(currentPiece)) {
				// Can't move down — move back; lock delay in game loop handles locking
				currentPiece.move(0, -1);
			} else {
				// Piece moved — award soft drop point and reset gravity counter
				this.game.scoreManager.addPoints(1);
				this.game.dropCounter = 0;
			}

			this.game.board.render(this.game.state);
		}
	}

	/**
	 * Rotate the piece
	 */
	rotate() {
		this.game.rotatePiece();
	}

	/**
	 * Hard drop the piece
	 */
	hardDrop() {
		this.game.hardDrop();
	}

	/**
	 * Hold the current piece
	 */
	holdPiece() {
		this.game.holdPiece();
	}

	/**
	 * Toggle pause state
	 */
	togglePause() {
		// Only toggle pause if the game is actually running
		if (this.game.state.isGameRunning) {
			console.log('Toggle pause triggered by key press');
			this.game.togglePause();
		}
	}
}
